import { useId, type ReactElement, type ReactNode } from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

export interface FieldProps {
  label: string;
  /** Field-level error. When present it replaces the hint and marks the control invalid. */
  error?: string;
  /** Guidance shown under the control while there is no error. */
  hint?: string;
  required?: boolean;
  /** Character counter, shown beside the label for length-capped fields. */
  counter?: { value: number; max: number };
  className?: string;
  /**
   * Receives the generated id and the `aria-describedby` the control must
   * carry, so labels and messages stay wired up without callers repeating it.
   */
  children: (props: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactElement;
}

/**
 * Label + control + message, with the accessibility plumbing handled once:
 *
 * ```tsx
 * <Field label="Tenant name" error={errors.name} required>
 *   {({ id, describedBy, invalid }) => (
 *     <Input id={id} aria-describedby={describedBy} invalid={invalid} />
 *   )}
 * </Field>
 * ```
 */
export function Field({
  label,
  error,
  hint,
  required,
  counter,
  className,
  children,
}: FieldProps): ReactNode {
  const id = useId();
  const messageId = `${id}-message`;
  const hasMessage = Boolean(error ?? hint);

  /* Only warn near the cap - a counter on every field from character one is
     noise, and the limits here are generous. */
  const nearLimit = counter ? counter.value >= counter.max * 0.9 : false;

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} required={required}>
          {label}
        </Label>

        {counter && nearLimit && (
          <span
            className={cn(
              'mb-1 text-2xs tabular-nums',
              counter.value >= counter.max ? 'text-danger' : 'text-content-subtle',
            )}
          >
            {counter.value}/{counter.max}
          </span>
        )}
      </div>

      {children({ id, describedBy: hasMessage ? messageId : undefined, invalid: Boolean(error) })}

      {hasMessage && (
        <p
          id={messageId}
          className={cn('mt-1 text-xs', error ? 'text-danger' : 'text-content-muted')}
          role={error ? 'alert' : undefined}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
